import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { isNaN } from 'underscore';
import { VideoTemplateEditorFeatures } from 'components/VideoTemplateEditor';
import { OnStyleChange } from 'components/VideoTemplateEditor/useOnStyleChange';
import { Step } from 'components/Wizard';
import { TranscriptionFormValue } from 'containers/ProjectWizards/types';
import useOnChange from 'hooks/useOnChange';
import usePodcastData from 'hooks/usePodcastData';
import { PodcastWorkflowTemplate } from 'redux/middleware/api/podcast-service/types';
import { createDefaultProjectNameSelector } from 'redux/modules/project/selectors';
import { selectTemplate } from 'redux/modules/template-wizard/actions';
import { templateSelector } from 'redux/modules/template-wizard/selectors';
import { PodcastIdentifier, TemplateType, VideoTypes } from 'types';
import { getAspectRatioName } from 'utils/aspect-ratio';
import CustomizeStep from '../../../CustomizeStep';
import { CustomizeStepView } from '../../../CustomizeStep/CustomizeStep';
import CustomizeStepCta, {
  CustomizeStepCtaProps,
} from '../../../CustomizeStep/CustomizeStepCta';
import { block } from '../../utils';
import { AudioFlowStep } from './types';

export interface UseCustomizeStepConfig {
  aspectRatio: number;
  durationMillis: number;
  onSubmit?: CustomizeStepCtaProps['onClick'];
  onTemplatesClick?: () => void;
  onTranscriptionChange: (value: TranscriptionFormValue) => void;
  podcastIdentifier?: PodcastIdentifier;
  source?: File | string;
  transcription: TranscriptionFormValue;
}

interface UseCustomizeStepResult {
  step: Step<AudioFlowStep.CUSTOMIZE>;
}

function getEditorFeatures(
  template: PodcastWorkflowTemplate,
): VideoTemplateEditorFeatures {
  const supportsShortClip = template?.compatibleVideoTypes.includes(
    VideoTypes.SHORT_CLIP,
  );

  return !template
    ? undefined
    : {
        progress: supportsShortClip || 'hidden',
        captions: true,
        templates: 'hidden',
      };
}

export default function useCustomizeStep({
  aspectRatio,
  durationMillis,
  onSubmit,
  onTemplatesClick,
  onTranscriptionChange,
  podcastIdentifier,
  source,
  transcription,
}: UseCustomizeStepConfig): UseCustomizeStepResult {
  const dispatch = useDispatch();
  const template = useSelector(templateSelector);
  const features = getEditorFeatures(template);
  const [view, setView] = useState<CustomizeStepView>('edit');
  const [
    templateStepAspectRatioName,
    setTemplateStepAspectRatioName,
  ] = useState(getAspectRatioName(aspectRatio));
  const { episodeId, podcastId } = podcastIdentifier ?? {};
  const { artworkUrl, episodeTitle, podcastTitle } = usePodcastData(
    podcastIdentifier,
  );

  const transcriptionEnabled = transcription?.transcribe;

  useOnChange(aspectRatio, {
    isEqual(newValue, oldValue) {
      return (isNaN(newValue) && isNaN(oldValue)) || newValue === oldValue;
    },
    onChange(value) {
      const name = getAspectRatioName(value);
      if (name !== templateStepAspectRatioName) {
        setTemplateStepAspectRatioName(name);
      }
    },
  });

  const defaultProjectName = useSelector(
    useMemo(
      () =>
        createDefaultProjectNameSelector({
          source,
          episodeId,
          podcastId,
        }),
      [episodeId, podcastId, source],
    ),
  );

  const handleTemplateSelect = useCallback(
    templateId => {
      dispatch(selectTemplate(templateId));
    },
    [dispatch],
  );

  const handleStyleChange: OnStyleChange = useCallback(
    (style, actionType) => {
      if (actionType === 'TRANSCRIPTION_CHANGE') {
        onTranscriptionChange(style.transcription);
      }
    },
    [onTranscriptionChange],
  );

  return {
    step: {
      completedName: 'Completed',
      component: (
        <CustomizeStep
          className={block('customize-step')}
          compatibilityTypes={[VideoTypes.FULL_EPISODE, VideoTypes.SHORT_CLIP]}
          defaults={{
            aspectRatio,
            keyAssets: {
              episodeTitle,
              podcastTitle,
              mainImage: artworkUrl,
            },
          }}
          podcastId={podcastId}
          episodeId={episodeId}
          features={features}
          key={template?.templateId}
          onChangeView={setView}
          onTemplatesClick={onTemplatesClick}
          onSelectAspectRatio={setTemplateStepAspectRatioName}
          onSelectTemplate={handleTemplateSelect}
          onStyleChange={handleStyleChange}
          renderCta={({ className, disabled, exportEditorState, state }) => (
            <CustomizeStepCta
              onClick={onSubmit}
              {...{
                className,
                disabled,
                durationMillis,
                exportEditorState,
                state,
                transcriptionEnabled,
                defaultProjectName,
              }}
            />
          )}
          templateId={template?.templateId}
          templateStepAspectRatioName={templateStepAspectRatioName}
          templateTypes={[
            TemplateType.HEADLINER_DEFAULT,
            TemplateType.USER_GENERATED,
          ]}
          transcription={transcription}
          transcriptionEnabled={transcriptionEnabled}
          view={view}
        />
      ),
      name: 'Customize',
      renderCancelButton: () => null,
      renderNextButton: () => null,
      stepId: AudioFlowStep.CUSTOMIZE,
      title: view === 'edit' ? undefined : 'Chooose a template',
    },
  };
}
